/* OsPlanGridViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./OsPlanGridViewRenderer");
const Registrations = require("../../../../../RegistrationNamespaces");
const DateComponent = require("./column_renderers/date/DateComponent");
const PlanStatusComponent = require("./column_renderers/plan_status/PlanStatusComponent");

const PagingOsPlanFeed = require("./PagingOsPlanFeed");

const _ = require("lodash");

class OsPlanGridViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        steveUtils,
        OsUpgradePlan,
        userSession,
        storageFactory,
        solutionsManager,
        modalUserFeedback,
        OsImageServerEntity,
        di
    ) {
        super(di);
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._steveUtils = steveUtils;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;
        this._solutionsManager = solutionsManager;
        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "osUpgradeExplorer";
        this._storageFactory = storageFactory;
        this._userGridData = undefined;
        this._osPlanEntity = OsUpgradePlan;
        this._osImageServerEntity = OsImageServerEntity;
        this._di = di;
        this._pagingOsPlanFeed = undefined;
        this._renderer = new Renderer(this);
        this._osPlanActionsForAdvancedFilter = this._getOsPlanDataForAdvancedFilter(
            this._osPlanEntity.OS_UPGRADE_ACTION
        );
        this._osPlanStatusForAdvancedFilter = this._getOsPlanDataForAdvancedFilter(
            this._osPlanEntity.OS_UPGRADE_STATUS
        );
        this._explorerState = {
            selectedIds: [],
        };
        this.columns = undefined;
        this._sessionStorage = undefined;
        this._isItFromDeviceExplorer = false;
    }

    get columnConfig() {
        if (this.columns) {
            return this.columns;
        }
        this.columns = {
            action: {
                label: "Action",
                width: 80,
                priority: 10,
                options: this._osPlanActionsForAdvancedFilter,
            },
            name: {
                label: "Name",
                width: 220,
                priority: 20,
            },
            description: {
                label: "Description",
                width: 300,
                priority: 30,
            },
            deviceCount: {
                label: "Total Devices",
                width: 110,
                priority: 40,
                validation: {
                    min: 0,
                },
            },
            deviceSuccessCount: {
                label: "Successful Devices",
                width: 110,
                priority: 40,
                validation: {
                    min: 0,
                },
            },
            deviceFailureCount: {
                label: "Failed Devices",
                width: 110,
                priority: 40,
                validation: {
                    min: 0,
                },
            },
            status: {
                label: "Plan Status",
                width: 120,
                priority: 50,
                columnRendererComponent: PlanStatusComponent,
                options: this._osPlanStatusForAdvancedFilter,
                context: {
                    _planStatusList: this._osPlanEntity.OS_UPGRADE_STATUS,
                },
            },
            createdBy: {
                label: "Created By",
                width: 120,
                priority: 60,
                updateFromEntity: true,
            },
            createdOn: {
                label: "Created On",
                width: 160,
                priority: 70,
                columnRendererComponent: DateComponent,
                updateFromEntity: true,
            },
            updatedBy: {
                label: "Updated By",
                width: 120,
                priority: 80,
            },
            updatedOn: {
                label: "Updated On",
                width: 160,
                priority: 90,
                columnRendererComponent: DateComponent,
            },
        };

        return this.columns;
    }

    get pagingFeed() {
        return this._pagingOsPlanFeed;
    }

    get selectedRows() {
        return this._renderer.selectedRows;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._solutionsManager.on(
            "activeSolutionChanged",
            (this._onSolutionChanged = this._onSolutionChanged.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._solutionsManager) {
            this._solutionsManager.removeListener("activeSolutionChanged", this._onSolutionChanged);
        }
    }

    _patchSorting(sorting) {
        // add second sorting order by name
        if (
            sorting &&
            Object.keys(sorting).length === 1 &&
            !sorting.name &&
            this.columnConfig.name
        ) {
            sorting.name = 1;
        }
    }

    _registerFeedListeners() {}

    _unregisterFeedListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        return this._storageFactory
            .whenStorageIsReady()
            .then(() => {
                this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "entity_grid_gl",
                    this._gridKey
                );
                this._sessionStorage = this._storageFactory.getSessionStorageInstance(
                    "entity_grid_gl",
                    `${this._gridKey}_explorerState`
                );
                let sessionData = this._sessionStorage.getData();
                if (_.get(sessionData.selectedIds, "isFrom", false)) {
                    this._isItFromDeviceExplorer = true;
                }
                this._explorerState = Object.assign({}, this._explorerState, sessionData);
            })
            .then(() => {
                this._pagingOsPlanFeed = new PagingOsPlanFeed({
                    feedKey: this._gridKey + "_feed",
                    userSession: this._userSession,
                    entityDef: this._osPlanEntity,
                    columnConfig: this.columnConfig,
                    filter:
                        _.get(this._userGridData.getBasicSearchData(), "filterQuery", {}) ||
                        this._userGridData.getAdvancedSearchData(),
                    sort: !_.isEmpty(this._userGridData.getSorting())
                        ? this._userGridData.getSorting()
                        : {updatedOn: -1},
                });
                this._registerListeners();
                return Promise.resolve();
            })
            .then(() => {
                if (this._solutionName === "os_manager") {
                    this._imageServersFeed = this._establishImageServersFeed(this.activeOrgId).then(
                        () => {
                            this._statusBar = this._initStatusBar();
                            return this.whenFeedIsReady();
                        }
                    );
                } else {
                    return this.whenFeedIsReady();
                }
            });
    }

    wasFeedPreviouslyInitialized() {
        return this._pagingOsPlanFeed.wasPreviouslyInitialized();
    }

    whenFeedIsReady() {
        return this._pagingOsPlanFeed.whenFeedReady();
    }

    get renderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    shutdown() {
        this._unregisterFeedListeners();
        this._unregisterListeners();
        let shutdowns = [
            _.invoke(this._renderer, "shutdown") || Promise.resolve(),
            _.invoke(this._pagingOsPlanFeed, "shutdown") || Promise.resolve(),
            _.invoke(this._sessionStorage, "shutdown") || Promise.resolve(),
        ];
        return Promise.all(shutdowns).then(
            () => {
                //this._gameEditor._unregisterPlanExplorerListeners(); // Unregister game editor events
                // game editor shouldn't be shutdown from here !!
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._steveUtils = undefined;
                this._explorerState = undefined;
                this._pagingOsPlanFeed = undefined;
                this._userSession = undefined;
                this._modalUserFeedback = undefined;
                this._solutionsManager = undefined;
                this._solutionName = undefined;
                this._gridKey = undefined;
                this._userGridData = undefined;
                this._sessionStorage = undefined;
                this._logger = undefined;
                this._isItFromDeviceExplorer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "OsPlanGridViewComponent.shutdown() error");
            }
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get explorerState() {
        return this._explorerState;
    }

    get activeOrgId() {
        return this._userSession.activeOrganization.id;
    }

    set explorerState(val) {
        this.setExplorerState(val);
    }

    setExplorerState(val) {
        this._explorerState = val;
    }

    updateExplorerState(property, val) {
        this._sessionStorage.setData(val, property);
        _.set(this._explorerState, property, val);
    }

    resetSelection(newIds) {
        let selectedIds = [];

        if (newIds && newIds.length) {
            newIds.forEach((id) => {
                selectedIds.push(id);
            });
        }

        this.renderer._osPlanEntityGrid.setSelectedRows(selectedIds);
        this.updateExplorerState("selectedIds", {selectedRows: selectedIds});
        this.renderer._renderAvailableActions();
    }

    loadAndScrollTo(entityId) {
        let that = this;
        this.pagingFeed.on("RequestedEntityFound", handleRequestedEntityFound);
        this.pagingFeed._requestEntity(entityId);

        function handleRequestedEntityFound(index) {
            that.renderer._osPlanEntityGrid.renderer.scrollToTheRow(index);
            that.pagingFeed.removeListener("RefreshView", handleRequestedEntityFound);
        }
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    onUserSorted(sortData) {
        let sort = {};

        const sortCriteria =
            this.columnConfig[sortData.sortColumn].valuePath ||
            this.columnConfig[sortData.sortColumn].path ||
            sortData.sortColumn;

        sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        this._patchSorting(sort);

        this._pagingOsPlanFeed.setSort(sort);
    }

    _onSolutionChanged(solution) {
        // whole pane will be reset, no need to make any action here
        this._solutionName = solution.name;
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _establishImageServersFeed(orgId) {
        if (!orgId) {
            return;
        }
        const query = {};
        const fields = [
            "enabled",
            "name",
            "identifier",
            "ip",
            "status",
            "percentFull",
            "isMain",
        ].reduce((fields, field) => Object.assign(fields, {[field]: true}), {});

        const sort = {createdOn: 1};
        if (this._imageServersFeed) {
            await this._imageServersFeed.whenInitialized();
            await this._imageServersFeed.reestablishFeed(orgId, query, fields, sort);
        } else {
            this._imageServersFeed = await this._osImageServerEntity.startAscendOsImageServersFeed(
                orgId,
                query,
                fields,
                sort
            );
            this._registerImageServersFeedListeners(this._imageServersFeed);
        }

        return this._imageServersFeed;
    }

    _initStatusBar() {
        this._defaultDiOpts = {
            defaultInjectables: ["$teve.logger", "eventEmitter.eventEmitter"],
        };

        let diOpts = _.cloneDeep(this._defaultDiOpts) || {};
        diOpts.injectableOverrides = {
            //"gameEditor.gameEditor": this
            "eventEmitter.eventEmitter": this,
        };

        return this._di.createInstance(Registrations.OS_PLAN_STATUS_BAR, diOpts);
    }

    _onImageServerFeedUpdate(feed) {
        this.emit("ImageServersUpdated", feed.getCacheAsArray());
    }

    _registerImageServersFeedListeners(feed) {
        const onFeedUpdate = _.debounce(this._onImageServerFeedUpdate.bind(this), 0);

        feed.on("ChangeFeedInitialized", (data, feed /*, reInit*/) => {
            onFeedUpdate(feed, "ChangeFeedInitialized");
        })
            .on("EntityInitialized", () => {
                onFeedUpdate(feed, "EntityInitialized");
            })
            .on("EntityPropertiesUpdated", (entity, patches, props, feed) => {
                onFeedUpdate(feed, "EntityPropertiesUpdated");
            })
            .on("EntityAdded", (entity, properties, feed) => {
                onFeedUpdate(feed, "EntityAdded");
            })
            .on("EntityRemoved", (entity, cause, feed) => {
                onFeedUpdate(feed, "EntityRemoved");
            })
            .once("ShutdownStarted", () => {
                onFeedUpdate.cancel();
                feed.removeListener("ChangeFeedInitialized", onFeedUpdate)
                    .removeListener("EntityInitialized", onFeedUpdate)
                    .removeListener("EntityPropertiesUpdated", onFeedUpdate)
                    .removeListener("EntityAdded", onFeedUpdate)
                    .removeListener("EntityRemoved", onFeedUpdate);
            });
    }

    _getOsPlanDataForAdvancedFilter(data) {
        let clonedData = _.clone(data);
        let newObj = {};
        for (let key in clonedData) {
            let value = {};
            value[key] = {
                name: key,
                title: clonedData[key],
            };
            newObj = Object.assign(newObj, value);
        }
        return newObj;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    openPlanInstanceEditor(ids, fullyQualifiedViewName) {
        ids = [].concat(ids);

        const id = ids[0];
        //let Plan = this._pagingOsPlanFeed.getOsPlanEntityById(id);
        this._osPlanEntity
            .loadOsUpgradePlan(this._userSession.activeOrganization.id, id, {name: true})
            .then((osPlan) =>
                this._gameEditor.launchPlanEditor(
                    {
                        fqn: fullyQualifiedViewName || Registrations.OS_PLAN_EDITOR_VIEW_DETAILS,
                    },
                    {
                        id: id,
                        title: _.get(osPlan, "name", ""),
                    }
                )
            )
            .catch((error) => this._logger.warn({error}));
    }

    getSelectedPlanIds() {
        return _.get(this._explorerState, "selectedIds", []);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OsPlanGridViewComponent;

OsPlanGridViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.utils",
    "$teve.entities.OsUpgradePlan",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.solutionsManager",
    "$teve.ui.userFeedback",
    "$teve.entities.OsImageServer",
    "$teve.di",
];

OsPlanGridViewComponent.$displayDetails = {
    type: "View",
    name: "",
    icon: "osmanager.os_plan",
    shown: false,
    order: 3,
};
